import { createContext, useContext, type ReactNode } from "react";

export type ChannelDesk = {
  base: string;
  role: string;
  summary: string;
  catalogueTitle: string;
  catalogueEmpty: string;
  availableWith: string;
  earnWhen: string;
  stockNote: string;
  stockEmpty: string;
  pendingPurchaseLabel: string;
  salesTitle: string;
  purchaseTitle: string;
  paymentTitle: string;
};

export const distributorDesk: ChannelDesk = {
  base: "/distributor",
  role: "Distributor",
  summary: "You buy from admin and sell to your wholesalers. Stock and commission move when an order is approved.",
  catalogueTitle: "Admin catalogue",
  catalogueEmpty: "No approved products are available from admin yet.",
  availableWith: "admin",
  earnWhen: "a wholesaler order is approved",
  stockNote: "This is channel stock credited when admin approves your order. It is separate from a store’s shelf.",
  stockEmpty: "No stock yet. Order from the catalogue and wait for admin approval.",
  pendingPurchaseLabel: "Orders with admin",
  salesTitle: "Sales to wholesalers",
  purchaseTitle: "Purchases from admin",
  paymentTitle: "Payments to admin",
};

export const retailerDesk: ChannelDesk = {
  base: "/store/supply",
  role: "Retailer",
  summary: "You buy from your wholesaler and bill customers from the store. Your commission is the product’s retail percent, credited when a customer sale completes.",
  catalogueTitle: "Wholesaler catalogue",
  catalogueEmpty: "Your wholesaler has no channel stock for approved products yet.",
  availableWith: "your wholesaler",
  earnWhen: "you bill a customer",
  stockNote: "This is channel stock credited when your wholesaler approves your order. Customer billing still uses the store shelf and does not debit this stock.",
  stockEmpty: "No channel stock yet. Order from the catalogue and wait for your wholesaler to approve.",
  pendingPurchaseLabel: "Orders with wholesaler",
  salesTitle: "Customer sales",
  purchaseTitle: "Purchases from wholesaler",
  paymentTitle: "Payments to wholesaler",
};

export const wholesalerDesk: ChannelDesk = {
  base: "/wholesaler",
  role: "Wholesaler",
  summary: "You buy from your distributor and sell to retailers. Your commission is the product’s wholesale percent, credited when you approve a retailer order.",
  catalogueTitle: "Distributor catalogue",
  catalogueEmpty: "Your distributor has no channel stock for approved products yet.",
  availableWith: "your distributor",
  earnWhen: "a retailer order is approved",
  stockNote: "This is channel stock credited when your distributor approves your order. It is separate from a store’s shelf.",
  stockEmpty: "No stock yet. Order from the catalogue and wait for your distributor to approve.",
  pendingPurchaseLabel: "Orders with distributor",
  salesTitle: "Sales to retailers",
  purchaseTitle: "Purchases from distributor",
  paymentTitle: "Payments to distributor",
};

const ChannelDeskContext = createContext<ChannelDesk>(distributorDesk);

export function ChannelDeskProvider({ desk, children }: { desk: ChannelDesk; children: ReactNode }) {
  return <ChannelDeskContext.Provider value={desk}>{children}</ChannelDeskContext.Provider>;
}

export function useChannelDesk() {
  return useContext(ChannelDeskContext);
}
